import {
  Notification,
  type NotificationBoardRow,
  NotificationBoardRowType,
  NotificationStatus,
} from '@copera/notification';
import { useBoardTableRowDialog } from '@modules/board/BoardTableRowDialog';
import {
  Avatar,
  Box,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
  styled,
} from '@mui/material';
import { Stack, alpha } from '@mui/system';
import { useNavigate } from 'react-router';
import { Iconify } from 'src/components/iconify';
import { FormatTime } from 'src/utils/formatTime';
import { useNotificationContent } from '../shared/useNotificationContent';
import { useUpdateNotificationMutation } from '../shared/useUpdateNotificationMutation';

type NotificationListItemProps = {
  item: Notification;
  onClose: () => void;
};

const UnreadDot = styled('span')(({ theme }) => ({
  display: 'block',
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: theme.palette.secondary.main,
  marginLeft: theme.spacing(1),
}));

const ItemContainer = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isRead',
})<{ isRead: boolean }>(({ theme, isRead }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  padding: theme.spacing(1.5, 2.5),
  ...(isRead && {
    backgroundColor: alpha(theme.palette.action.disabled, 0.08),
    '&:hover': {
      backgroundColor: alpha(theme.palette.action.disabled, 0.04),
    },
  }),
  '& .MuiListItemText-root': { marginLeft: 0 },
}));

export const NotificationListItem = ({
  item,
  onClose,
}: NotificationListItemProps) => {
  const navigate = useNavigate();

  const { getNotificationContent } = useNotificationContent();

  const { icon, title, description, navigateTo } = getNotificationContent(item);

  const { updateNotification } = useUpdateNotificationMutation();

  const { showBoardTableRowDialog } = useBoardTableRowDialog();

  const handleClick = async () => {
    if (item.type === NotificationBoardRowType.NEW_COMMENT) {
      const { data } = item as NotificationBoardRow;
      return showBoardTableRowDialog({
        viewId: '',
        boardId: data?.boardId,
        tableId: data?.tableId,
        rowId: data?.rowId || '',
      });
    }

    if (item.type === NotificationBoardRowType.ASSIGNED) {
      const { data } = item as NotificationBoardRow;
      return showBoardTableRowDialog({
        viewId: '',
        boardId: data?.boardId,
        tableId: data?.tableId,
        rowId: data?._id || '',
      });
    }
    if (navigateTo) {
      navigate(navigateTo);
      onClose();
    }

    await updateNotification({
      id: item._id || '',
      status: NotificationStatus.READ,
    });
  };

  return (
    <ItemContainer
      isRead={item.status === NotificationStatus.READ}
      onClick={handleClick}
    >
      <ListItemAvatar>
        <Avatar sx={{ backgroundColor: 'background.neutral' }}>
          <Iconify icon={icon} width={24} />
        </Avatar>
      </ListItemAvatar>

      <ListItemText
        disableTypography
        primary={
          <Typography variant="subtitle2">
            {title}
            <br />
            <Typography
              component="span"
              variant="body2"
              sx={{ color: 'text.secondary' }}
            >
              {description}
            </Typography>
          </Typography>
        }
        secondary={
          <Stack
            direction="row"
            sx={{ mt: 0.5, typography: 'caption', color: 'text.disabled' }}
          >
            <Iconify icon="eva:clock-fill" width={16} sx={{ mr: 0.5 }} />
            <Typography variant="caption">
              {FormatTime.ToNow(item.createdAt || '')}
            </Typography>
          </Stack>
        }
      />
      {item.status === NotificationStatus.UNREAD && (
        <Box margin="auto">
          <UnreadDot />
        </Box>
      )}
    </ItemContainer>
  );
};
